/**
 * Shared helpers for Teams AI Assist and home-screen meeting-notes chat streaming.
 */

export function extractAssistantTextFromJson(jsonData: unknown): string {
  if (jsonData == null) return "";
  if (typeof jsonData === "string") return jsonData;
  if (typeof jsonData !== "object") return String(jsonData);

  const o = jsonData as Record<string, unknown>;

  const pickString = (v: unknown): string =>
    typeof v === "string" && v.length > 0 ? v : "";

  const direct =
    o.content ?? o.answer ?? o.text ?? o.response ?? o.output ?? o.result;
  const d = pickString(direct);
  if (d) return d;
  if (direct != null && typeof direct !== "object") return String(direct);

  const msg = o.message;
  if (typeof msg === "string") return pickString(msg);
  if (msg && typeof msg === "object") {
    const inner = (msg as { content?: unknown }).content;
    if (pickString(inner)) return pickString(inner);
  }

  const delta = o.delta;
  if (delta && typeof delta === "object") {
    const inner = (delta as { content?: unknown }).content;
    if (pickString(inner)) return pickString(inner);
  }

  const choices = o.choices;
  if (Array.isArray(choices) && choices[0] != null && typeof choices[0] === "object") {
    const c0 = choices[0] as Record<string, unknown>;
    const t = pickString(c0.text);
    if (t) return t;
    const dlt = c0.delta;
    if (dlt && typeof dlt === "object") {
      const inner = (dlt as { content?: unknown }).content;
      if (pickString(inner)) return pickString(inner);
    }
    const m = c0.message;
    if (m && typeof m === "object") {
      const inner = (m as { content?: unknown }).content;
      if (pickString(inner)) return pickString(inner);
    }
  }

  /** Meeting-notes chat history rows use `llm_response: { response: "..." }`. */
  const llmResponse = o.llm_response;
  if (llmResponse !== undefined && llmResponse !== null) {
    const fromLlm = extractAssistantTextFromJson(llmResponse);
    if (fromLlm.trim()) return fromLlm;
  }

  const nested = o.data;
  if (nested !== undefined) return extractAssistantTextFromJson(nested);

  return "";
}

export interface MeetingNotesChatSseEvent {
  /** Assistant-visible text to append for this line (empty when the event has no text). */
  content: string;
  /** From the `type: "complete"` event so feedback API can reference the bot turn. */
  messageId?: string;
  /** From the `type: "complete"` event (image_path field) — used for visual citations. */
  imagePath?: unknown;
  /** True when this line carried a `type: "complete"` envelope. */
  isComplete?: boolean;
}

/**
 * Parse one SSE line from meeting-notes chat. Returns text plus the metadata that lives on
 * the terminal `type: "complete"` event (notably `message_id`, which the feedback API requires).
 */
export function parseMeetingNotesChatSseEvent(
  line: string
): MeetingNotesChatSseEvent {
  const trimmed = line.trim();
  if (!trimmed || trimmed === "data: [DONE]") return { content: "" };

  let jsonData: unknown = null;
  let rawFallback = "";

  if (trimmed.startsWith("data: ")) {
    const jsonStr = trimmed.slice(6);
    if (jsonStr === "[DONE]") return { content: "" };
    try {
      jsonData = JSON.parse(jsonStr);
    } catch {
      rawFallback = jsonStr;
    }
  } else {
    try {
      jsonData = JSON.parse(trimmed);
    } catch {
      rawFallback = trimmed;
    }
  }

  if (rawFallback) return { content: rawFallback };

  const obj =
    jsonData && typeof jsonData === "object"
      ? (jsonData as Record<string, unknown>)
      : null;

  const isComplete = obj?.type === "complete";
  const messageId =
    obj && typeof obj.message_id === "string" && obj.message_id
      ? obj.message_id
      : undefined;
  const imagePath = obj?.image_path;

  // `complete` envelopes carry metadata, not assistant text — don't append their JSON to the bot.
  const content = isComplete ? "" : extractAssistantTextFromJson(jsonData);

  return { content, messageId, imagePath, isComplete };
}

/** Backwards-compatible thin wrapper: returns only the text portion of an SSE line. */
export function parseMeetingNotesChatSseLine(line: string): string {
  return parseMeetingNotesChatSseEvent(line).content;
}
